import { useCallback, useSyncExternalStore } from 'react'

/**
 * The three-state theme rule — docs/DESIGN-SYSTEM.md §4.
 *
 * `tokens.css` defines light on bare `:root`, then redefines the palette twice
 * for dark: under `@media (prefers-color-scheme: dark)` and under
 * `:root[data-theme="dark"]`, so an explicit choice wins in both directions.
 *
 * | Setting   | Renders                                             |
 * |-----------|-----------------------------------------------------|
 * | `light`   | Light — SS1's palette, and the project default      |
 * | `dark`    | AgencyOS layered-dark                               |
 * | `system`  | Follows the OS; light unless the OS says dark       |
 *
 * `system` removes the stamp entirely rather than resolving it to a value, so a
 * viewer who changes their OS setting is followed without a reload.
 *
 * ⚠️ **One store, not per-hook state.** The first version held the value in
 * `useState`, which was fine while `App` was the only caller — but the moment a
 * second one existed (the user menu's picker) each would have had its own copy,
 * and `App`'s would have gone stale the instant the menu changed it. The DOM
 * would still have looked right, because `apply()` writes to the document, so
 * the bug would have been invisible until something read `theme` and got a lie.
 * `useSyncExternalStore` over a module-level value keeps every caller agreeing.
 */
export type Theme = 'light' | 'dark' | 'system'

export const THEMES: readonly Theme[] = ['light', 'dark', 'system']

export const THEME_LABELS: Record<Theme, string> = {
  light: 'Light',
  dark: 'Dark',
  // Named for what it does rather than what it is: "System" alone reads as a
  // section heading in a menu.
  system: 'Match System',
}

const STORAGE_KEY = 'pca-theme'

function readStored(): Theme {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark' || stored === 'system') {
      return stored
    }
  } catch {
    // A private window or blocked site data. Fall through to the default.
  }
  return 'system'
}

function apply(theme: Theme): void {
  const root = document.documentElement
  if (theme === 'system') {
    root.removeAttribute('data-theme')
  } else {
    root.setAttribute('data-theme', theme)
  }
}

let current: Theme = readStored()
const listeners = new Set<() => void>()

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function getSnapshot(): Theme {
  return current
}

/** Applied immediately, so the stamp is right before React's first paint. */
apply(current)

export function setTheme(next: Theme): void {
  current = next
  apply(next)
  try {
    window.localStorage.setItem(STORAGE_KEY, next)
  } catch {
    // Per-viewer convenience only — never state the app depends on.
  }
  for (const listener of listeners) listener()
}

export function useTheme(): { theme: Theme; setTheme: (next: Theme) => void } {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  return { theme, setTheme: useCallback(setTheme, []) }
}
